import { useCallback, useEffect, useMemo, useState } from 'react';
import find from 'lodash/find';
import isArray from 'lodash/isArray';
import isNull from 'lodash/isNull';
import isUndefined from 'lodash/isUndefined';

import { FetchFinCategoriesFilters } from '../../../categories/categoriesTypes';
import { FinCategoryParentFinCategoryID } from '../../finCategoriesTypes';
import { ID } from '../../../../types';
import {
  MultiSelectChangeCallbackType,
  MultiSelectDataType
} from '../../../../helpers/MultiSelect/types';

import { usePreviousValue } from '../../../../common/hooks/usePreviousValue';
import { useTranslate } from '../../../../common/hooks/useTranslate';

import { formsFields } from '../../../../locales/keys';

import {
  FETCH_FIN_CATEGORIES_QUERY,
  FetchFinCategoriesQueryResponse
} from '../../query/fetchFinCategories.query';

import { useCachePaginatedFinCategories } from '../../hooks/useCachePaginatedFinCategories';

import { FinCategoryCache } from '../../FinCategoryCache';

import { MultiSelect } from '../../../../helpers/MultiSelect';

interface FinCategoriesMultiSelectProps {
  value: ID[] | ID | null;
  autoFocus?: boolean;
  closeMenuOnSelect?: boolean;
  disabled?: boolean;
  multi?: boolean;
  withNoneOption?: boolean;
  onChange: (value: ID[] | ID | null) => void;
  name?: string;
  menuPosition?: 'absolute' | 'fixed';
  parentFinCategoryId?: FinCategoryParentFinCategoryID;
  withPortal?: boolean;
}

function FinCategoriesMultiSelect({
  value,
  autoFocus,
  closeMenuOnSelect,
  disabled,
  multi = true,
  withNoneOption = false,
  onChange,
  name,
  menuPosition,
  parentFinCategoryId,
  withPortal
}: FinCategoriesMultiSelectProps) {
  const { t } = useTranslate();

  const hasValue = multi ? isArray(value) && value.length > 0 : value != null;
  const [menuOpened, setMenuOpened] = useState(false);
  const fetchEnabled = hasValue || menuOpened;

  const handleMenuOpen = useCallback(() => {
    setMenuOpened(true);
  }, []);

  const initialFilters = useMemo<FetchFinCategoriesFilters>(() => {
    if (isUndefined(parentFinCategoryId)) {
      return {};
    }

    if (isNull(parentFinCategoryId)) {
      return { parentFinCategoryId: { isNull: true } };
    }

    if (parentFinCategoryId === 'isNotNull') {
      return { parentFinCategoryId: { isNotNull: true } };
    }

    return {
      parentFinCategoryId: {
        eq: parentFinCategoryId
      }
    };
  }, [parentFinCategoryId]);

  const {
    finCategories,
    finCategoriesLoading,
    finCategoriesError,
    filterFinCategories
  } = useCachePaginatedFinCategories<FetchFinCategoriesQueryResponse>({
    query: FETCH_FIN_CATEGORIES_QUERY,
    cacheKey: FinCategoryCache.selectFieldCacheKey(),
    initialFilters,
    initialLimit: 1000,
    options: { enabled: fetchEnabled }
  });

  const prevParentFinCategoryId = usePreviousValue(parentFinCategoryId);

  useEffect(() => {
    if (prevParentFinCategoryId !== parentFinCategoryId) {
      filterFinCategories(initialFilters);
    }
  }, [
    filterFinCategories,
    initialFilters,
    parentFinCategoryId,
    prevParentFinCategoryId
  ]);

  const tagsData = useMemo<MultiSelectDataType[]>(() => {
    const items = finCategories.map((tag) => ({
      value: tag.id,
      label: tag.name
    }));
    if (withNoneOption && !multi) {
      return [{ value: null, label: t(formsFields.none) }, ...items];
    }
    return items;
  }, [finCategories, withNoneOption, multi, t]);

  const handleChange = useCallback<MultiSelectChangeCallbackType>(
    (selected: MultiSelectDataType) => {
      if (multi) {
        onChange(isArray(selected) ? selected.map((item) => item.value) : []);
      } else {
        onChange(
          selected && !isArray(selected) ? (selected.value as ID) : null
        );
      }
    },
    [multi, onChange]
  );

  const multiSelectValue = useMemo(() => {
    if (multi) {
      return isArray(value)
        ? value.map((item) => find(tagsData, ['value', item]))
        : [];
    }
    return !isArray(value) ? find(tagsData, ['value', value]) ?? null : null;
  }, [multi, value, tagsData]);

  return (
    <MultiSelect
      error={finCategoriesError}
      inputWrapperClassName="block"
      isClearable={multi}
      onChange={handleChange}
      value={multiSelectValue}
      multi={multi}
      isSearchable={true}
      isLoading={finCategoriesLoading}
      optionsLoading={finCategoriesLoading}
      data={tagsData}
      classNamePrefix="av"
      autoFocus={autoFocus}
      disabled={disabled}
      name={name}
      menuPosition={menuPosition}
      onMenuOpen={handleMenuOpen}
      withPortal={withPortal}
      closeMenuOnSelect={closeMenuOnSelect}
    />
  );
}

export default FinCategoriesMultiSelect;
